/**
 * AudioPlayer Component
 *
 * Audio player with playback controls for synchronized transcript display.
 * Features play/pause, seek, volume, skip, and playback speed controls.
 */

import {
  useRef,
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
  RotateCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export interface AudioPlayerRef {
  seekTo: (timeMs: number) => void;
  getCurrentTime: () => number;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  isPlaying: () => boolean;
}

interface AudioPlayerProps {
  src: string;
  onTimeUpdate?: (currentTimeMs: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onLoadedMetadata?: (durationMs: number) => void;
  initialTime?: number;
  variant?: "default" | "sticky";
}

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

/**
 * Format milliseconds to time string (H:MM:SS or M:SS)
 */
function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(
  (
    {
      src,
      onTimeUpdate,
      onPlay,
      onPause,
      onEnded,
      onLoadedMetadata,
      initialTime = 0,
      variant = "default",
    },
    ref
  ) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [showRateMenu, setShowRateMenu] = useState(false);

    // Expose methods via ref
    const seekTo = useCallback((timeMs: number) => {
      if (audioRef.current) {
        audioRef.current.currentTime = timeMs / 1000;
        setCurrentTime(timeMs);
      }
    }, []);

    // Toggle play/pause
    const togglePlay = useCallback(() => {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play();
        }
      }
    }, [isPlaying]);

    useImperativeHandle(
      ref,
      () => ({
        seekTo,
        getCurrentTime: () => currentTime,
        play: () => audioRef.current?.play(),
        pause: () => audioRef.current?.pause(),
        togglePlay,
        isPlaying: () => isPlaying,
      }),
      [seekTo, currentTime, togglePlay, isPlaying]
    );

    // Skip forward/backward
    const skip = useCallback((seconds: number) => {
      if (audioRef.current) {
        audioRef.current.currentTime = Math.max(
          0,
          Math.min(
            audioRef.current.duration,
            audioRef.current.currentTime + seconds
          )
        );
      }
    }, []);

    // Handle time updates
    const handleTimeUpdate = useCallback(() => {
      if (audioRef.current) {
        const timeMs = audioRef.current.currentTime * 1000;
        setCurrentTime(timeMs);
        onTimeUpdate?.(timeMs);
      }
    }, [onTimeUpdate]);

    // Handle metadata loaded
    const handleLoadedMetadata = useCallback(() => {
      if (audioRef.current) {
        const durationMs = audioRef.current.duration * 1000;
        setDuration(durationMs);
        onLoadedMetadata?.(durationMs);

        if (initialTime > 0) {
          audioRef.current.currentTime = initialTime / 1000;
        }
      }
    }, [initialTime, onLoadedMetadata]);

    // Handle seek via progress bar
    const handleSeek = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const timeMs = parseFloat(e.target.value);
        seekTo(timeMs);
      },
      [seekTo]
    );

    // Handle volume change
    const handleVolumeChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const vol = parseFloat(e.target.value);
        setVolume(vol);
        if (audioRef.current) {
          audioRef.current.volume = vol;
        }
        setIsMuted(vol === 0);
      },
      []
    );

    // Toggle mute
    const toggleMute = useCallback(() => {
      if (audioRef.current) {
        audioRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
      }
    }, [isMuted]);

    // Change playback rate
    const changePlaybackRate = useCallback((rate: number) => {
      if (audioRef.current) {
        audioRef.current.playbackRate = rate;
        setPlaybackRate(rate);
      }
      setShowRateMenu(false);
    }, []);

    // Handle play event
    const handlePlay = useCallback(() => {
      setIsPlaying(true);
      onPlay?.();
    }, [onPlay]);

    // Handle pause event
    const handlePause = useCallback(() => {
      setIsPlaying(false);
      onPause?.();
    }, [onPause]);

    // Handle ended event
    const handleEnded = useCallback(() => {
      setIsPlaying(false);
      onEnded?.();
    }, [onEnded]);

    // Close rate menu on outside click
    useEffect(() => {
      if (showRateMenu) {
        const handleClickOutside = () => setShowRateMenu(false);
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
      }
    }, [showRateMenu]);

    // Calculate progress percentage
    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

    // Container styling based on variant
    const containerClassName =
      variant === "sticky"
        ? "py-2 px-3"
        : "bg-card border rounded-lg py-3 px-4 shadow-sm";

    return (
      <div className={containerClassName}>
        <audio
          ref={audioRef}
          src={src}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          preload="metadata"
        />

        {/* Single row layout - responsive sizing */}
        <div className="flex items-center gap-1.5 md:gap-2">
          {/* Volume toggle - hidden on mobile */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMute}
            className="hidden md:flex h-7 w-7 text-muted-foreground hover:text-muted-foreground shrink-0"
          >
            {isMuted ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
            <span className="sr-only">{isMuted ? "Unmute" : "Mute"}</span>
          </Button>

          {/* Hidden volume slider for accessibility */}
          <input
            type="range"
            min={0}
            max={1}
            step={0.1}
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="sr-only"
            aria-label="Volume"
          />

          {/* Skip back */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => skip(-15)}
            className="h-7 w-7 text-muted-foreground hover:text-muted-foreground shrink-0"
          >
            <RotateCcw className="h-3.5 w-3.5 md:h-4 md:w-4" />
            <span className="sr-only">Skip back 15 seconds</span>
          </Button>

          {/* Play/Pause button - prominent dark style */}
          <button
            type="button"
            onClick={togglePlay}
            className="h-8 w-8 md:h-9 md:w-9 rounded-full bg-foreground hover:bg-foreground/90 text-background flex items-center justify-center shrink-0 transition-colors"
          >
            {isPlaying ? (
              <Pause className="h-3.5 w-3.5 md:h-4 md:w-4" />
            ) : (
              <Play className="h-3.5 w-3.5 md:h-4 md:w-4 ml-0.5" />
            )}
            <span className="sr-only">{isPlaying ? "Pause" : "Play"}</span>
          </button>

          {/* Skip forward */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => skip(15)}
            className="h-7 w-7 text-muted-foreground hover:text-muted-foreground shrink-0"
          >
            <RotateCw className="h-3.5 w-3.5 md:h-4 md:w-4" />
            <span className="sr-only">Skip forward 15 seconds</span>
          </Button>

          {/* Current time */}
          <span className="text-xs md:text-sm text-muted-foreground tabular-nums shrink-0 min-w-[32px] md:min-w-[40px] text-right">
            {formatTime(currentTime)}
          </span>

          {/* Progress bar - vertically centered */}
          <div className="flex-1 relative flex items-center min-w-0">
            <input
              type="range"
              min={0}
              max={duration}
              value={currentTime}
              onChange={handleSeek}
              aria-label="Playback progress"
              className="w-full h-1 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:w-2.5 md:[&::-webkit-slider-thumb]:h-3 md:[&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-foreground [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:h-2.5 [&::-moz-range-thumb]:w-2.5 md:[&::-moz-range-thumb]:h-3 md:[&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-foreground [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
              style={{
                background: `linear-gradient(to right, #1f2937 ${progressPercent}%, #e5e7eb ${progressPercent}%)`,
              }}
            />
          </div>

          {/* Duration */}
          <span className="text-xs md:text-sm text-muted-foreground tabular-nums shrink-0 min-w-[32px] md:min-w-[40px]">
            {formatTime(duration)}
          </span>

          {/* Playback speed */}
          <div className="relative shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowRateMenu(!showRateMenu);
              }}
              className="text-xs md:text-sm font-medium text-muted-foreground hover:text-foreground px-1.5 md:px-2 h-7"
            >
              {playbackRate}x
            </Button>

            {showRateMenu && (
              <div className="absolute bottom-full right-0 mb-1 bg-card border rounded-lg shadow-lg py-1 z-10">
                {PLAYBACK_RATES.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      changePlaybackRate(rate);
                    }}
                    className={`block w-full px-4 py-1.5 text-sm text-left hover:bg-accent ${
                      playbackRate === rate
                        ? "bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400"
                        : ""
                    }`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

AudioPlayer.displayName = "AudioPlayer";
